import React, { useState, useEffect, useRef } from 'react'
import { Collapse, Button, Form, Row, Col, Select, InputNumber, Checkbox, Tooltip, Progress, Space } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import * as com from '../../Common'
import { estimatePayment, estimatorOverrides, estimatorPrincipal, getDefaultDTIRate } from './dtiPayment'
import { formatDollars, parseDollars } from '../ProductFilterModal/moneyInput'
import './EstimatedPayment.css'

export default function EstimatedPayment(props) {
    // baseValues: the 1003-derived values (Reset all goes back to them). restoreValues: the
    // LO's unsaved edits, when the calculator comes back from being minimized.
    const { onPaymentDataChange, activeKey, onCollapseChange, initialValues, baseValues, restoreValues } = props
    const [form] = Form.useForm()
    const [totalPayment, setTotalPayment] = useState(0)
    const [internalExpanded, setInternalExpanded] = useState(false)
    
    // 使用activeKey或内部状态来控制展开
    const isExpanded = activeKey && activeKey.length > 0 ? true : internalExpanded
    
    // 当外部activeKey变化时，同步内部状态
    useEffect(() => {
        if (activeKey && activeKey.length > 0) {
            setInternalExpanded(true)
        }
    }, [activeKey])
    const [paymentBreakdown, setPaymentBreakdown] = useState({
        principalAndInterest: 0,
        propertyTaxes: 0,
        insurance: 0,
        hoaFees: 0,
        pmi: 0,
        total: 0
    })
    
    // 辅助函数：安全解析数字，空字符串返回0
    const safeParseFloat = (value) => {
        if (value === null || value === undefined || value === '') return 0
        const parsed = parseFloat(value)
        return isNaN(parsed) ? 0 : parsed
    }
    

    
    // 辅助函数：解析数字（移除逗号和空格）
    const parseNumber = (value) => {
        if (value === null || value === undefined || value === '') return 0
        return parseFloat(String(value).replace(/[,\s]/g, '')) || 0
    }

    // 计算 LTV (Loan-to-Value ratio)
    const calculateLTV = (values) => {
        const propertyValue = parseNumber(values.propertyValue)
        const principal = estimatorPrincipal(values)
        return propertyValue > 0 ? principal / propertyValue : 0
    }

    // 检查是否需要 PMI (LTV > 0.8)
    // The editable PMI box is for conventional loans; FHA / USDA premiums are computed, VA has none.
    const isConventional = (values) => (values.loanType || 'conventional') === 'conventional'
    const needsPMI = (values) => {
        return isConventional(values) && calculateLTV(values) > 0.8
    }

    // Taxes and insurance % are annual rates of the property value; MI % is annual of the
    // loan amount (how they are quoted). No property value, no percentages.
    const calculateInitialPercentages = (values) => {
        const propertyValue = parseNumber(values.propertyValue)
        const loan = Math.max(0, estimatorPrincipal(values))
        const annualOf = (monthly, base) => (base > 0 ? Math.round((parseNumber(monthly) * 12 / base) * 100 * 1000) / 1000 : 0)
        return {
            ...values,
            downpaymentPercent: propertyValue > 0 ? Math.round((parseNumber(values.downpayment) / propertyValue) * 100 * 100) / 100 : 0,
            propertyTaxesPercent: annualOf(values.propertyTaxes, propertyValue),
            insurancePercent: annualOf(values.insurance, propertyValue),
            pmiPercent: annualOf(values.pmi, loan),
        }
    }
    const monthlyFromAnnual = (percent, base) => Math.round(base > 0 ? (percent / 100) * base / 12 : 0)

    // 使用 state 管理表单值（包含计算出的百分比）
    // 根据 customerId 确定默认利率
    const defaultInterestRate = getDefaultDTIRate(com.getCustomerId())
    const [formValues, setFormValues] = useState(() => calculateInitialPercentages(restoreValues || initialValues || {
        loanPurpose: 'purchase',
        propertyValue: 300000,
        interestRate: defaultInterestRate,
        loanTerm: 30,
        downpayment: 0,
        currentLoanBalance: 0,
        paydown: 0,
        cashoutAmount: 0,
        propertyTaxes: 0,
        insurance: 0,
        hoaFees: 0,
        pmi: 0,
        interestOnly: false
    }))
    const latestValues = useRef(formValues)
    latestValues.current = formValues

    // 初始化时计算付款
    useEffect(() => {
        calculatePayment(formValues)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps
    
    // When the source of a field changes (guideline → loan type, a newly picked rate), take
    // just those fields; everything else the LO typed stays.
    const previousInitial = useRef(initialValues)
    useEffect(() => {
        const previous = previousInitial.current
        previousInitial.current = initialValues
        if (!initialValues || previous === initialValues) return
        const changed = Object.keys(initialValues).filter((key) => initialValues[key] !== previous?.[key])
        if (!changed.length) return
        const next = calculateInitialPercentages({ ...latestValues.current, ...Object.fromEntries(changed.map((key) => [key, initialValues[key]])) })
        setFormValues(next)
        form.setFieldsValue(next)
        calculatePayment(next)
    }, [initialValues]) // eslint-disable-line react-hooks/exhaustive-deps

    // Reset all 功能
    const handleResetAll = () => {
        const resetValues = calculateInitialPercentages(baseValues || initialValues || {
            loanPurpose: 'purchase',
            propertyValue: 300000,
            interestRate: defaultInterestRate,
            loanTerm: 30,
            downpayment: 0,
            currentLoanBalance: 0,
            paydown: 0,
            cashoutAmount: 0,
            propertyTaxes: 0,
            insurance: 0,
            hoaFees: 0,
            pmi: 0,
            interestOnly: false
        })
        
        // 重置表单值状态
        setFormValues(resetValues)
        
        // 重置表单字段
        form.setFieldsValue(resetValues)
        
        // 重新计算付款
        calculatePayment(resetValues)
    }

    // 当propertyValue变化时，更新百分比值
    useEffect(() => {
        if (formValues.propertyValue) {
            const updatedValues = calculateInitialPercentages(formValues)
            setFormValues(updatedValues)
            form.setFieldsValue(updatedValues)
        }
    }, [formValues.propertyValue]) // eslint-disable-line react-hooks/exhaustive-deps

    // MI is hidden, not cleared, when LTV is 80% or less: estimatePayment ignores it there,
    // and it comes back if the LTV goes up again.

    const RATE_SOURCES = {
        saved: 'Entered in this estimate',
        picked: 'From the rate selection',
        selected: 'Selected loan',
        recommended: 'Highest recommended rate',
    }
    const rateSourceText = () => {
        if (initialValues && Number(formValues.interestRate) !== Number(initialValues.interestRate)) return 'Entered in this estimate'
        return RATE_SOURCES[initialValues?.rateSource] || ''
    }
    const FIELD_NAMES = {
        loanPurpose: 'loan purpose', propertyValue: 'property value', downpayment: 'down payment', loanAmount: 'loan amount',
        currentLoanBalance: 'current balance', paydown: 'paydown', cashoutAmount: 'cash-out', interestRate: 'rate', loanTerm: 'term',
        propertyTaxes: 'taxes', insurance: 'insurance', hoaFees: 'HOA', pmi: 'MI', interestOnly: 'interest only',
    }
    // Fields that differ from the 1003 (saved with the DTI settings on Done, not in the 1003).
    const editedFields = baseValues ? Object.keys(estimatorOverrides(baseValues, formValues)) : []

    const customHeader = (type) => {
        return (
            <div className='estimated-monthly-payment-header'>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div className="estimated-monthly-payment-header-item" style={{ gap: isExpanded ? 5 : 13 }}>
                        Estimated monthly payment {!isExpanded && '(PITIA): $ ' + com.commaizeFloat(totalPayment)}
                    </div>
                    {isExpanded && (
                        <span 
                            className='estimated-monthly-payment-header-reset' 
                            onClick={(e) => {
                                e.stopPropagation()
                                handleResetAll()
                            }}
                        >
                            Reset all
                        </span>
                    )}
                </div>
            </div>   
        )
    }

    const calculatePayment = (values) => {
        const { total: roundedTotal, breakdown } = estimatePayment(values)
        const { principalAndInterest: roundedPrincipalAndInterest, propertyTaxes, insurance, hoaFees, pmi: calculatedPmi } = breakdown

        setTotalPayment(roundedTotal)
        setPaymentBreakdown({
            ...breakdown,
            principalAndInterest: roundedPrincipalAndInterest,
            propertyTaxes,
            insurance,
            hoaFees,
            pmi: calculatedPmi,
            total: roundedTotal
        })

        // Call callback to pass data to parent component
        if (onPaymentDataChange) {
            onPaymentDataChange({
                total: roundedTotal,
                breakdown,
                formValues: values
            })
        }
    }

    const getSelectIcon = () => {
        return (
            <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M19.3431 13.5384L18.415 12.6104L14.3939 16.6315L10.3744 12.612L9.44629 13.54L13.4658 17.5596L13.4653 17.5601L14.3934 18.4882L14.3939 18.4876L14.396 18.4898L15.3241 17.5617L15.322 17.5596L19.3431 13.5384Z" fill="#6E6E70"/>
            </svg>
        )
    }

    return (
        <>
            <Collapse 
                className='estimated-monthly-payment-collapse' 
                expandIcon={({ isActive }) => <img className={`${isActive ? 'expand' : ''} collapse-icon`} src="/images/dti-calculator-arrow.svg" /> } 
                expandIconPosition="end"
                activeKey={isExpanded ? ['estimated-payment'] : []}
                onChange={(activeKeys) => {
                    // 更新内部状态
                    setInternalExpanded(activeKeys.length > 0)
                    // 通知外部状态变化
                    if (onCollapseChange) {
                        onCollapseChange(activeKeys)
                    }
                }}
            >
                <Collapse.Panel key="estimated-payment" className={`estimated-monthly-payment-collapse-item`} header={customHeader('income')}>
                    {editedFields.length > 0 && (
                        <div className='estimated-monthly-payment-edited'>
                            Different from the 1003: {editedFields.map((key) => FIELD_NAMES[key] || key).join(', ')}.
                            These stay with the DTI calculator (saved on Done or by the rate selection); the 1003 is not changed. Reset all goes back to the 1003.
                        </div>
                    )}
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={formValues}
                    >
                        <Row gutter={20}>
                            <Col span={6}>
                                <Form.Item
                                    name="loanPurpose"
                                    label="Loan Purpose"
                                >
                                    <Select
                                        className='estimated-monthly-payment-select'
                                        value={formValues.loanPurpose}
                                        onChange={(value) => {
                                            // Carry the current loan amount over, so a switch does not drop P&I to $0.
                                            const carried = Math.max(0, estimatorPrincipal(formValues))
                                            const newValues = { ...formValues, loanPurpose: value, loanAmount: parseNumber(formValues.loanAmount) || carried }
                                            setFormValues(newValues)
                                            form.setFieldsValue({ loanPurpose: value })
                                            calculatePayment(newValues)
                                        }}
                                        suffixIcon={getSelectIcon()}
                                    >
                                        <Select.Option value="purchase">Purchase</Select.Option>
                                        <Select.Option value="refinance">Rate/term refinance</Select.Option>
                                        <Select.Option value="cashoutrefinance">Cash-out refinance</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    name="propertyValue"
                                    label="Property Value"
                                >
                                    <InputNumber
                                        prefix="$"
                                        className='estimated-monthly-payment-input'
                                        style={{ width: '100%' }}
                                        formatter={formatDollars}
                                        parser={parseDollars}
                                        value={formValues.propertyValue}
                                        controls={false}
                                        onChange={(value) => {
                                            if (value !== null && value !== undefined) {
                                                const newValues = { ...formValues, propertyValue: value }
                                                setFormValues(newValues)
                                                form.setFieldsValue({ propertyValue: value })
                                                calculatePayment(newValues)
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    name="interestRate"
                                    label={formValues.loanPurpose === 'purchase' ? "Interest Rate (%)" : "New interest rate (%)"}
                                >
                                    <InputNumber
                                        suffix="%"
                                        className='estimated-monthly-payment-input'
                                        style={{ width: '100%' }}
                                        min={0}
                                        max={50}
                                        step={1}
                                        controls={false}
                                        value={formValues.interestRate}
                                        onChange={(value) => {
                                            if (value !== null && value !== undefined) {
                                                const newValues = { ...formValues, interestRate: value }
                                                setFormValues(newValues)
                                                form.setFieldsValue({ interestRate: value })
                                                calculatePayment(newValues)
                                            }
                                        }}
                                    />
                                    {/* Where the rate comes from; "See rates" opens the Rates tab. */}
                                    <div className='estimated-monthly-payment-rate-source'>
                                        {rateSourceText()}
                                        {props.toRates && (
                                            <span className='estimated-monthly-payment-link' onClick={() => props.toRates()}>See rates</span>
                                        )}
                                    </div>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    name="loanTerm"
                                    label={formValues.loanPurpose === 'purchase' ? "Loan Term" : "New loan term"}
                                >
                                    <Select
                                        className='estimated-monthly-payment-select'
                                        value={formValues.loanTerm}
                                        onChange={(value) => {
                                            const newValues = { ...formValues, loanTerm: value }
                                            setFormValues(newValues)
                                            form.setFieldsValue({ loanTerm: value })
                                            calculatePayment(newValues)
                                        }}
                                        suffixIcon={getSelectIcon()}
                                    >
                                        <Select.Option value={30}>Fix 30 Year</Select.Option>
                                        <Select.Option value={25}>Fix 25 Year</Select.Option>
                                        <Select.Option value={20}>Fix 20 Year</Select.Option>
                                        <Select.Option value={15}>Fix 15 Year</Select.Option>
                                        <Select.Option value={10}>Fix 10 Year</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                                                                                {formValues.loanPurpose === 'purchase' && (
                            <Col span={6}>
                                <Form.Item
                                    name="downpayment"
                                    label="Downpayment"
                                >
                                    <Space.Compact style={{ width: '100%' }}>
                                        <InputNumber
                                            prefix="$"
                                            className='estimated-monthly-payment-input'
                                            style={{ flex: 1 }}
                                            min={0}
                                            formatter={formatDollars}
                                            parser={parseDollars}
                                            value={formValues.downpayment}
                                            controls={false}
                                            onChange={(value) => {
                                                if (value !== null && value !== undefined) {
                                                    const propertyValue = parseNumber(formValues.propertyValue)
                                                    const percent = Math.round((propertyValue > 0 ? (value / propertyValue) * 100 : 0) * 100) / 100
                                                    const newValues = {
                                                        ...formValues,
                                                        downpayment: value,
                                                        downpaymentPercent: percent
                                                    }
                                                    setFormValues(newValues)
                                                    form.setFieldsValue({
                                                        downpayment: value,
                                                        downpaymentPercent: percent
                                                    })
                                                    calculatePayment(newValues)
                                                }
                                            }}
                                        />
                                            <Form.Item name="downpaymentPercent" noStyle>
                                                <InputNumber
                                                    suffix="%"
                                                    className='estimated-monthly-payment-input'
                                                    style={{ width: '74px' }}
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    value={formValues.downpaymentPercent}
                                                    disabled={!(parseNumber(formValues.propertyValue) > 0)}
                                                    controls={false}
                                                    onChange={(value) => {
                                                        // 当值为空时，设置为0
                                                        const percentValue = value !== null && value !== undefined ? value : 0
                                                        const propertyValue = parseNumber(formValues.propertyValue)
                                                        const amount = Math.round(propertyValue > 0 ? (percentValue / 100) * propertyValue : 0)
                                                        const newValues = {
                                                            ...formValues,
                                                            downpayment: amount,
                                                            downpaymentPercent: percentValue
                                                        }
                                                        setFormValues(newValues)
                                                        // 只更新金额字段，不更新百分比字段，避免循环更新
                                                        form.setFieldsValue({
                                                            downpayment: amount
                                                        })
                                                        calculatePayment(newValues)
                                                    }}
                                                />
                                            </Form.Item>
                                        </Space.Compact>
                                    </Form.Item>
                                </Col>
                            )}
                                                            {(formValues.loanPurpose === 'refinance' || formValues.loanPurpose === 'cashoutrefinance') && (
                                <Col span={6}>
                                    <Form.Item
                                        name="currentLoanBalance"
                                        label="Current loan balance"
                                    >
                                        <InputNumber
                                            prefix="$"
                                            className='estimated-monthly-payment-input'
                                            style={{ width: '100%' }}
                                            min={0}
                                            formatter={formatDollars}
                                            parser={parseDollars}
                                            value={formValues.currentLoanBalance}
                                            controls={false}
                                            onChange={(value) => {
                                                if (value !== null && value !== undefined) {
                                                    const newValues = {
                                                        ...formValues,
                                                        currentLoanBalance: value,
                                                        loanAmount: formValues.loanPurpose === 'cashoutrefinance'
                                                            ? value + parseNumber(formValues.cashoutAmount)
                                                            : Math.max(0, value - parseNumber(formValues.paydown)),
                                                    }
                                                    form.setFieldsValue({ loanAmount: newValues.loanAmount })
                                                    setFormValues(newValues)
                                                    form.setFieldsValue({ currentLoanBalance: value })
                                                    calculatePayment(newValues)
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                            {formValues.loanPurpose === 'refinance' && (
                                <Col span={6}>
                                    <Form.Item
                                        name="paydown"
                                        label="Paydown principal"
                                    >
                                        <InputNumber
                                            prefix="$"
                                            className='estimated-monthly-payment-input'
                                            style={{ width: '100%' }}
                                            min={0}
                                            max={parseNumber(formValues.currentLoanBalance) > 0 ? parseNumber(formValues.currentLoanBalance) : undefined}
                                            formatter={formatDollars}
                                            parser={parseDollars}
                                            value={formValues.paydown}
                                            controls={false}
                                            onChange={(value) => {
                                                if (value !== null && value !== undefined) {
                                                    const newValues = { ...formValues, paydown: value, loanAmount: Math.max(0, parseNumber(formValues.currentLoanBalance) - value) }
                                                    form.setFieldsValue({ loanAmount: newValues.loanAmount })
                                                    setFormValues(newValues)
                                                    form.setFieldsValue({ paydown: value })
                                                    calculatePayment(newValues)
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                                                            {formValues.loanPurpose === 'cashoutrefinance' && (
                                <Col span={6}>
                                    <Form.Item
                                        name="cashoutAmount"
                                        label="Cash-out amount"
                                    >
                                        <InputNumber
                                            prefix="$"
                                            className='estimated-monthly-payment-input'
                                            style={{ width: '100%' }}
                                            min={0}
                                            max={parseNumber(formValues.propertyValue) - parseNumber(formValues.currentLoanBalance) > 0 ? parseNumber(formValues.propertyValue) - parseNumber(formValues.currentLoanBalance) : undefined}
                                            formatter={formatDollars}
                                            parser={parseDollars}
                                            value={formValues.cashoutAmount}
                                            controls={false}
                                            onChange={(value) => {
                                                if (value !== null && value !== undefined) {
                                                    const newValues = { ...formValues, cashoutAmount: value, loanAmount: parseNumber(formValues.currentLoanBalance) + value }
                                                    form.setFieldsValue({ loanAmount: newValues.loanAmount })
                                                    setFormValues(newValues)
                                                    form.setFieldsValue({ cashoutAmount: value })
                                                    calculatePayment(newValues)
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                            {(formValues.loanPurpose === 'refinance' || formValues.loanPurpose === 'cashoutrefinance') && (
                                <Col span={6}>
                                    <Form.Item
                                        name="loanAmount"
                                        label="New loan amount"
                                    >
                                        <InputNumber
                                            prefix="$"
                                            className='estimated-monthly-payment-input'
                                            style={{ width: '100%' }}
                                            min={0}
                                            formatter={formatDollars}
                                            parser={parseDollars}
                                            value={formValues.loanAmount}
                                            controls={false}
                                            onChange={(value) => {
                                                if (value !== null && value !== undefined) {
                                                    const newValues = { ...formValues, loanAmount: value }
                                                    setFormValues(newValues)
                                                    form.setFieldsValue({ loanAmount: value })
                                                    calculatePayment(newValues)
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                            <Col span={6}>
                                <Form.Item
                                    name="propertyTaxes"
                                    label="Property Taxes (monthly)"
                                >
                                    <Space.Compact style={{ width: '100%' }}>
                                        <InputNumber
                                            prefix="$"
                                            className='estimated-monthly-payment-input'
                                            style={{ flex: 1 }}
                                            formatter={formatDollars}
                                            parser={parseDollars}
                                            value={formValues.propertyTaxes}
                                            controls={false}
                                            onChange={(value) => {
                                                if (value !== null && value !== undefined) {
                                                    const propertyValue = parseNumber(formValues.propertyValue)
                                                    const percent = propertyValue > 0 ? Math.round((value * 12 / propertyValue) * 100 * 1000) / 1000 : 0
                                                    const newValues = {
                                                        ...formValues,
                                                        propertyTaxes: value,
                                                        propertyTaxesPercent: percent
                                                    }
                                                    setFormValues(newValues)
                                                    form.setFieldsValue({
                                                        propertyTaxes: value,
                                                        propertyTaxesPercent: percent
                                                    })
                                                    calculatePayment(newValues)
                                                }
                                            }}
                                        />
                                        <Form.Item name="propertyTaxesPercent" noStyle>
                                            <InputNumber
                                                suffix="%/yr"
                                                className='estimated-monthly-payment-input'
                                                style={{ width: '92px' }}
                                                min={0}
                                                max={100}
                                                step={1}
                                                value={formValues.propertyTaxesPercent}
                                                disabled={!(parseNumber(formValues.propertyValue) > 0)}
                                                controls={false}
                                                onChange={(value) => {
                                                    // 当值为空时，设置为0
                                                    const percentValue = value !== null && value !== undefined ? value : 0
                                                    const propertyValue = parseNumber(formValues.propertyValue)
                                                    const amount = monthlyFromAnnual(percentValue, propertyValue)
                                                    const newValues = {
                                                        ...formValues,
                                                        propertyTaxes: amount,
                                                        propertyTaxesPercent: percentValue
                                                    }
                                                    setFormValues(newValues)
                                                    // 只更新金额字段，不更新百分比字段，避免循环更新
                                                    form.setFieldsValue({
                                                        propertyTaxes: amount
                                                    })
                                                    calculatePayment(newValues)
                                                }}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    name="insurance"
                                    label="Insurance (monthly)"
                                >
                                    <Space.Compact style={{ width: '100%' }}>
                                        <InputNumber
                                            prefix="$"
                                            className='estimated-monthly-payment-input'
                                            style={{ flex: 1 }}
                                            formatter={formatDollars}
                                            parser={parseDollars}
                                            value={formValues.insurance}
                                            controls={false}
                                            onChange={(value) => {
                                                if (value !== null && value !== undefined) {
                                                    const propertyValue = parseNumber(formValues.propertyValue)
                                                    const percent = propertyValue > 0 ? Math.round((value * 12 / propertyValue) * 100 * 1000) / 1000 : 0
                                                    const newValues = {
                                                        ...formValues,
                                                        insurance: value,
                                                        insurancePercent: percent
                                                    }
                                                    setFormValues(newValues)
                                                    form.setFieldsValue({
                                                        insurance: value,
                                                        insurancePercent: percent
                                                    })
                                                    calculatePayment(newValues)
                                                }
                                            }}
                                        />
                                        <Form.Item name="insurancePercent" noStyle>
                                            <InputNumber
                                                suffix="%/yr"
                                                className='estimated-monthly-payment-input'
                                                style={{ width: '92px' }}
                                                min={0}
                                                max={100}
                                                step={1}
                                                value={formValues.insurancePercent}
                                                disabled={!(parseNumber(formValues.propertyValue) > 0)}
                                                controls={false}
                                                onChange={(value) => {
                                                    // 当值为空时，设置为0
                                                    const percentValue = value !== null && value !== undefined ? value : 0
                                                    const propertyValue = parseFloat(String(formValues.propertyValue).replace(/[,\s]/g, '')) || 0
                                                    const amount = monthlyFromAnnual(percentValue, propertyValue)
                                                    const newValues = {
                                                        ...formValues,
                                                        insurance: Math.round(amount),
                                                        insurancePercent: percentValue
                                                    }
                                                    setFormValues(newValues)
                                                    // 只更新金额字段，不更新百分比字段，避免循环更新
                                                    form.setFieldsValue({
                                                        insurance: Math.round(amount)
                                                    })
                                                    calculatePayment(newValues)
                                                }}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    name="hoaFees"
                                    label="HOA Fees (monthly)"
                                >
                                    <InputNumber
                                        prefix="$"
                                        className='estimated-monthly-payment-input'
                                        style={{ width: '100%' }}
                                        formatter={formatDollars}
                                        parser={parseDollars}
                                        value={formValues.hoaFees}
                                        controls={false}
                                        onChange={(value) => {
                                            if (value !== null && value !== undefined) {
                                                const newValues = { ...formValues, hoaFees: value }
                                                setFormValues(newValues)
                                                form.setFieldsValue({ hoaFees: value })
                                                calculatePayment(newValues)
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            {needsPMI(formValues) && (
                                <Col span={6}>
                                    <Form.Item
                                        name="pmi"
                                        label={
                                            <Tooltip title={`LTV: ${(calculateLTV(formValues) * 100).toFixed(1)}% (PMI required when LTV > 80%)`}>
                                                <span>PMI (monthly)</span>
                                            </Tooltip>
                                        }
                                    >
                                        <Space.Compact style={{ width: '100%' }}>
                                            <InputNumber
                                                prefix="$"
                                                className='estimated-monthly-payment-input'
                                                style={{ flex: 1 }}
                                                formatter={formatDollars}
                                                parser={parseDollars}
                                                value={formValues.pmi}
                                                controls={false}
                                                onChange={(value) => {
                                                    if (value !== null && value !== undefined) {
                                                        const propertyValue = parseFloat(String(formValues.propertyValue).replace(/[,\s]/g, '')) || 0
                                                        const loan = Math.max(0, estimatorPrincipal(formValues)); const percent = loan > 0 ? (value * 12 / loan) * 100 : 0
                                                        const newValues = {
                                                            ...formValues,
                                                            pmi: value,
                                                            pmiPercent: Math.round(percent * 100) / 100
                                                        }
                                                        setFormValues(newValues)
                                                        form.setFieldsValue({
                                                            pmi: value,
                                                            pmiPercent: Math.round(percent * 100) / 100
                                                        })
                                                        calculatePayment(newValues)
                                                    }
                                                }}
                                            />
                                            <Form.Item name="pmiPercent" noStyle>
                                                <InputNumber
                                                    suffix="%/yr"
                                                    className='estimated-monthly-payment-input'
                                                    style={{ width: '92px' }}
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    value={formValues.pmiPercent}
                                                    controls={false}
                                                    onChange={(value) => {
                                                        // 当值为空时，设置为0
                                                        const percentValue = value !== null && value !== undefined ? value : 0
                                                        const propertyValue = parseFloat(String(formValues.propertyValue).replace(/[,\s]/g, '')) || 0
                                                        const amount = monthlyFromAnnual(percentValue, Math.max(0, estimatorPrincipal(formValues)))
                                                        const newValues = {
                                                            ...formValues,
                                                            pmi: Math.round(amount),
                                                            pmiPercent: percentValue
                                                        }
                                                        setFormValues(newValues)
                                                        // 只更新金额字段，不更新百分比字段，避免循环更新
                                                        form.setFieldsValue({
                                                            pmi: Math.round(amount)
                                                        })
                                                        calculatePayment(newValues)
                                                    }}
                                                />
                                            </Form.Item>
                                        </Space.Compact>
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                        {/* Own row, no label: in the grid it wrapped alone under a blank label row. */}
                        <Form.Item className='estimated-monthly-payment-interest-only' name="interestOnly" valuePropName="checked">
                            <Checkbox
                                checked={formValues.interestOnly}
                                onChange={(e) => {
                                    const newValues = { ...formValues, interestOnly: e.target.checked }
                                    setFormValues(newValues)
                                    form.setFieldsValue({ interestOnly: e.target.checked })
                                    calculatePayment(newValues)
                                }}
                            >
                                Interest Only
                            </Checkbox>
                        </Form.Item>
                    </Form>
                    {(paymentBreakdown.upfrontFee > 0 || ['annual premium', 'product', 'entered'].includes(paymentBreakdown.miSource)) && (
                        <div style={{ color: '#6E6E70', fontSize: 13, marginTop: 4 }}>
                            {paymentBreakdown.upfrontFee > 0 && (
                                <span>
                                    {paymentBreakdown.upfrontLabel} {(paymentBreakdown.upfrontRate * 100).toFixed(2)}% (${com.commaizeFloat(paymentBreakdown.upfrontFee)}) financed:
                                    P&I is on ${com.commaizeFloat(paymentBreakdown.totalLoanAmount)}.{' '}
                                </span>
                            )}
                            {paymentBreakdown.miSource === 'annual premium' && (
                                <span>MI ${com.commaizeFloat(paymentBreakdown.pmi)}/mo = {(paymentBreakdown.annualMIRate * 100).toFixed(2)}% annual premium.</span>
                            )}
                            {paymentBreakdown.miSource === 'product' && (
                                <span>MI ${com.commaizeFloat(paymentBreakdown.pmi)}/mo from the selected product.</span>
                            )}
                            {paymentBreakdown.miSource === 'entered' && (
                                <span>MI ${com.commaizeFloat(paymentBreakdown.pmi)}/mo as entered here.</span>
                            )}
                        </div>
                    )}
                    <div className='estimated-monthly-payment-divider'></div>
                    
                    {/* Payment Breakdown */}
                    <div className='estimated-monthly-payment-total'>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <div className='estimated-monthly-payment-total-title'>
                                Total monthly payment{formValues.interestOnly ? ' (Interest only)' : ''}
                            </div>
                            <div className='estimated-monthly-payment-total-amount'>
                                $ {com.commaizeFloat(paymentBreakdown.total)}
                            </div>
                        </div>
                        
                        <div className='estimated-monthly-payment-total-breakdown'>
                            <div style={{ display: 'flex', alignItems: 'center', lineHeight: 'normal', marginBottom: 4 }}>
                                <span style={{ fontFamily: 'Inter', fontSize: '14px', color: '#586570', fontWeight: 500 }}>Breakdown</span>
                            </div>
                            <div style={{ display: 'flex', height: 16, borderRadius: 10, overflow: 'hidden' }}>
                                <Tooltip title={`${formValues.interestOnly ? 'Interest' : 'Principal & Interest'}: $${com.commaizeFloat(paymentBreakdown.principalAndInterest)}`}>
                                    <div
                                        style={{
                                            flex: `${paymentBreakdown.principalAndInterest / paymentBreakdown.total * 100}%`,
                                            background: '#5A7BFC',
                                            position: 'relative',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title={`Property Taxes: $${com.commaizeFloat(paymentBreakdown.propertyTaxes)}`}>
                                    <div
                                        style={{
                                            flex: `${paymentBreakdown.propertyTaxes / paymentBreakdown.total * 100}%`,
                                            background: '#C3CDF2',
                                            position: 'relative',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title={`Insurance: $${com.commaizeFloat(paymentBreakdown.insurance)}`}>
                                    <div
                                        style={{
                                            flex: `${paymentBreakdown.insurance / paymentBreakdown.total * 100}%`,
                                            background: '#5247A7',
                                            position: 'relative',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title={`HOA Fees: $${com.commaizeFloat(paymentBreakdown.hoaFees)}`}>
                                    <div
                                        style={{
                                            flex: `${paymentBreakdown.hoaFees / paymentBreakdown.total * 100}%`,
                                            background: '#D9B4C2',
                                            position: 'relative',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title={`MI: $${com.commaizeFloat(paymentBreakdown.pmi)}`}>
                                    <div
                                        style={{
                                            flex: `${paymentBreakdown.pmi / paymentBreakdown.total * 100}%`,
                                            background: '#DBDCDE',
                                            position: 'relative',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Tooltip>
                            </div>
                            
                            {/* Each part's amount sits under its name so LOs can read it
                                off without hovering the bar. Parts that are $0 (no HOA,
                                no MI at 80% LTV) are left out; P&I always shows. */}
                            <div className='estimated-monthly-payment-legend'>
                                {[
                                    { label: formValues.interestOnly ? 'Interest' : 'P&I', amount: paymentBreakdown.principalAndInterest, color: '#5A7BFC', always: true },
                                    { label: 'Tax', amount: paymentBreakdown.propertyTaxes, color: '#C3CDF2' },
                                    { label: 'Insurance', amount: paymentBreakdown.insurance, color: '#5247A7' },
                                    { label: 'HOA', amount: paymentBreakdown.hoaFees, color: '#D9B4C2' },
                                    { label: 'MI', amount: paymentBreakdown.pmi, color: '#DBDCDE' },
                                ].filter((part) => part.always || Number(part.amount) > 0).map((part) => (
                                    <div key={part.label} className='estimated-monthly-payment-legend-item'>
                                        <div className='estimated-monthly-payment-legend-name'>
                                            <span className='estimated-monthly-payment-legend-dot' style={{ background: part.color }} />
                                            {part.label}
                                        </div>
                                        <div className='estimated-monthly-payment-legend-amount'>
                                            ${com.commaize(Math.round(Number(part.amount) || 0))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Collapse.Panel>
            </Collapse>
        </>
    )
}